import { Fragment, useEffect, useState } from 'react';
import {
  apiClient,
  type CreateIdentityProviderDto,
  type IdentityProvider,
  type IdpProviderType,
} from '../lib/api-client';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/Dialog';
import { Input } from '../components/ui/Input';

const selectClass =
  'h-9 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

function errText(err: unknown, fallback: string): string {
  if (!(err instanceof Error) || !err.message) return fallback;
  try {
    const parsed = JSON.parse(err.message) as { message?: unknown };
    if (typeof parsed.message === 'string' && parsed.message) return parsed.message;
    if (Array.isArray(parsed.message)) return parsed.message.map(String).join(', ');
  } catch {
    return err.message;
  }
  return err.message;
}

function emptyForm(): CreateIdentityProviderDto {
  return {
    alias: 'azure-ad',
    displayName: 'Microsoft',
    providerType: 'azure-entra',
    tenantId: '',
    clientId: '',
    clientSecret: '',
    authorizationUrl: '',
    tokenUrl: '',
    userInfoUrl: '',
  };
}

function canSave(form: CreateIdentityProviderDto): boolean {
  if (!form.alias || !form.displayName || !form.clientId || !form.clientSecret) return false;
  if ((form.providerType ?? 'azure-entra') === 'azure-entra') return !!form.tenantId?.trim();
  return !!(form.authorizationUrl && form.tokenUrl && form.userInfoUrl);
}

export function IdentityProvidersPage() {
  const [idps, setIdps] = useState<IdentityProvider[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<CreateIdentityProviderDto>(emptyForm);
  const [pendingRemove, setPendingRemove] = useState<IdentityProvider | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setIdps(await apiClient.listPlatformIdentityProviders());
    } catch (err) {
      setError(errText(err, 'Failed to load identity providers'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function save() {
    setSaving(true);
    setError(null);
    const providerType: IdpProviderType = form.providerType ?? 'azure-entra';
    const dto: CreateIdentityProviderDto = {
      alias: form.alias.trim(),
      displayName: form.displayName.trim(),
      providerType,
      clientId: form.clientId.trim(),
      clientSecret: form.clientSecret,
    };
    if (providerType === 'azure-entra') dto.tenantId = form.tenantId?.trim();
    else {
      dto.authorizationUrl = form.authorizationUrl?.trim();
      dto.tokenUrl = form.tokenUrl?.trim();
      dto.userInfoUrl = form.userInfoUrl?.trim();
    }
    try {
      await apiClient.createPlatformIdentityProvider(dto);
      setAdding(false);
      await load();
    } catch (err) {
      setError(errText(err, 'Failed to save identity provider'));
    } finally {
      setSaving(false);
    }
  }

  async function remove(idp: IdentityProvider) {
    try {
      await apiClient.deletePlatformIdentityProvider(idp.alias);
      setPendingRemove(null);
      await load();
    } catch (err) {
      setError(errText(err, 'Failed to delete identity provider'));
      setPendingRemove(null);
    }
  }

  const providerType = form.providerType ?? 'azure-entra';

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Identity Providers</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Broker Microsoft Entra ID or generic OIDC through Keycloak for platform sign-in. Tenant-specific providers are configured on the Users page.
        </p>
      </div>
      {error ? (
        <div className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">
          {error}
        </div>
      ) : null}
      <Card>
        <CardContent className="grid gap-4 pt-6">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-base font-semibold">Brokered providers</h2>
            <Button
              size="sm"
              onClick={() => {
                setForm(emptyForm());
                setAdding(true);
              }}
            >
              Add provider
            </Button>
          </div>
          {adding ? (
            <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
              <div className="grid max-w-lg gap-3">
                <label className="grid gap-1 text-sm">
                  <span className="text-xs text-gray-500">Provider type</span>
                  <select
                    className={selectClass}
                    value={providerType}
                    onChange={(e) => setForm((f) => ({ ...f, providerType: e.target.value as IdpProviderType }))}
                  >
                    <option value="azure-entra">Azure Entra ID</option>
                    <option value="oidc">Generic OIDC</option>
                  </select>
                </label>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="grid gap-1 text-sm">
                    <span className="text-xs text-gray-500">Alias</span>
                    <Input placeholder="azure-ad" value={form.alias} onChange={(e) => setForm((f) => ({ ...f, alias: e.target.value }))} />
                  </label>
                  <label className="grid gap-1 text-sm">
                    <span className="text-xs text-gray-500">Display name</span>
                    <Input placeholder="Microsoft" value={form.displayName} onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))} />
                  </label>
                </div>
                {providerType === 'azure-entra' ? (
                  <label className="grid gap-1 text-sm">
                    <span className="text-xs text-gray-500">Azure tenant ID</span>
                    <Input
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      value={form.tenantId ?? ''}
                      onChange={(e) => setForm((f) => ({ ...f, tenantId: e.target.value }))}
                    />
                  </label>
                ) : null}
                <label className="grid gap-1 text-sm">
                  <span className="text-xs text-gray-500">Client ID</span>
                  <Input value={form.clientId} onChange={(e) => setForm((f) => ({ ...f, clientId: e.target.value }))} />
                </label>
                <label className="grid gap-1 text-sm">
                  <span className="text-xs text-gray-500">Client secret</span>
                  <Input type="password" autoComplete="new-password" value={form.clientSecret} onChange={(e) => setForm((f) => ({ ...f, clientSecret: e.target.value }))} />
                </label>
                {providerType === 'oidc' ? (
                  <>
                    <label className="grid gap-1 text-sm">
                      <span className="text-xs text-gray-500">Authorization URL</span>
                      <Input value={form.authorizationUrl ?? ''} onChange={(e) => setForm((f) => ({ ...f, authorizationUrl: e.target.value }))} />
                    </label>
                    <label className="grid gap-1 text-sm">
                      <span className="text-xs text-gray-500">Token URL</span>
                      <Input value={form.tokenUrl ?? ''} onChange={(e) => setForm((f) => ({ ...f, tokenUrl: e.target.value }))} />
                    </label>
                    <label className="grid gap-1 text-sm">
                      <span className="text-xs text-gray-500">User info URL</span>
                      <Input value={form.userInfoUrl ?? ''} onChange={(e) => setForm((f) => ({ ...f, userInfoUrl: e.target.value }))} />
                    </label>
                  </>
                ) : null}
              </div>
              <div className="mt-3 flex gap-2">
                <Button size="sm" disabled={!canSave(form) || saving} onClick={() => void save()}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : null}
          {loading ? <p className="text-sm text-gray-500">Loading...</p> : null}
          {!loading && idps.length === 0 ? (
            <p className="text-sm text-gray-500">No identity providers yet. Add Azure Entra ID to enable Microsoft SSO.</p>
          ) : null}
          {!loading && idps.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs text-gray-500 dark:border-gray-800">
                    <th className="py-2 pr-3">Name</th>
                    <th className="py-2 pr-3">Alias</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2" />
                  </tr>
                </thead>
                <tbody>
                  {idps.map((idp) => (
                    <Fragment key={idp.alias}>
                      <tr className="border-b border-gray-100 dark:border-gray-800">
                        <td className="py-2 pr-3 font-medium">{idp.displayName}</td>
                        <td className="py-2 pr-3 font-mono text-xs">{idp.alias}</td>
                        <td className="py-2 pr-3">
                          <Badge variant={idp.enabled ? 'success' : 'warning'}>{idp.enabled ? 'Enabled' : 'Disabled'}</Badge>
                        </td>
                        <td className="py-2 text-right">
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setPendingRemove(idp)}>
                            Remove
                          </Button>
                        </td>
                      </tr>
                      {idp.brokerRedirectUri ? (
                        <tr className="border-b border-gray-100 dark:border-gray-800">
                          <td colSpan={4} className="py-2 text-xs text-gray-500">
                            Azure redirect URI: <code className="break-all">{idp.brokerRedirectUri}</code>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </CardContent>
      </Card>
      <Dialog open={!!pendingRemove} onOpenChange={(open) => { if (!open) setPendingRemove(null); }}>
        <DialogContent maxWidth="md">
          <DialogHeader>
            <DialogTitle>Remove identity provider</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Remove identity provider &quot;{pendingRemove?.displayName}&quot;? Microsoft SSO will stop working until another provider is added.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPendingRemove(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => pendingRemove && void remove(pendingRemove)}>Remove</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
